import React, { useEffect, useState } from "react";

/**
 * How a grid shows a failed query (BUG-20260804-jobs-grid-zeroes-on-missing-optional-column).
 * An empty grid is never the rendering of a failed query: with no rows the
 * grid shows QueryErrorPanel; with rows still on screen from an earlier load,
 * QueryErrorBanner says the refresh failed and how old those rows are.
 * QueryWarningBanner: the view runs without its optional columns because the
 * live model lacks one; the rows are current, those cells are blank.
 */

const ERROR_RED = "#ff6b6b";
const WARNING_AMBER = "#ffb74d";

function formatAge(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} h ago`;
}

/** The whole failure as plain text, for pasting into a ticket or a message. */
function queryErrorReport(error: string, query?: string, lastRefreshedAt?: number): string {
  const lines = [
    "Query failed",
    `Page: ${window.location.href}`,
    `At: ${new Date().toISOString()}`,
  ];
  if (lastRefreshedAt) lines.push(`Last successful load: ${new Date(lastRefreshedAt).toISOString()}`);
  lines.push(`Error: ${error}`);
  if (query) lines.push("Query:", query);
  return lines.join("\n");
}

function columnsUnavailableReport(columns: string[], reason: string): string {
  return [
    "Columns unavailable (the grid runs without them)",
    `Page: ${window.location.href}`,
    `At: ${new Date().toISOString()}`,
    `Columns: ${columns.join(", ")}`,
    `Reason: ${reason}`,
  ].join("\n");
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const linkButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "inherit",
  font: "inherit",
  textDecoration: "underline",
  cursor: "pointer",
  flexShrink: 0,
};

/** "Copy details": copies the report and says whether it worked. */
function CopyDetails({ report }: { report: () => string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (state === "idle") return;
    const t = setTimeout(() => setState("idle"), 2000);
    return () => clearTimeout(t);
  }, [state]);
  return (
    <button
      type="button"
      style={linkButtonStyle}
      onClick={async () => setState((await copyText(report())) ? "copied" : "failed")}
    >
      {state === "copied" ? "✓ Copied" : state === "failed" ? "Copy failed" : "Copy details"}
    </button>
  );
}

function useNow(periodMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), periodMs);
    return () => clearInterval(t);
  }, [periodMs]);
  return now;
}

export function QueryErrorPanel({
  error,
  query,
  retryEveryMs,
}: {
  error: string;
  query?: string;
  retryEveryMs?: number;
}) {
  const [showQuery, setShowQuery] = useState(false);
  return (
    <div
      role="alert"
      className="query-error-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 8,
        maxWidth: 760,
        margin: "0 auto",
        padding: "16px 20px",
        border: `1px solid ${ERROR_RED}66`,
        borderRadius: 6,
        background: "rgba(255, 107, 107, 0.08)",
        color: "#e0e0e0",
        textAlign: "left",
        pointerEvents: "auto",
      }}
    >
      <div style={{ color: ERROR_RED, fontWeight: 600 }}>⚠ Query failed</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.85rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {error}
      </div>
      <div style={{ display: "flex", gap: 12, fontSize: "0.8rem", opacity: 0.75 }}>
        {retryEveryMs ? <span>Retrying every {Math.round(retryEveryMs / 1000)} s</span> : null}
        {query ? (
          <button type="button" onClick={() => setShowQuery((v) => !v)} style={linkButtonStyle}>
            {showQuery ? "▾ Hide query" : "▸ Show query"}
          </button>
        ) : null}
        <CopyDetails report={() => queryErrorReport(error, query)} />
      </div>
      {showQuery && query ? (
        <pre
          style={{
            margin: 0,
            maxHeight: 200,
            width: "100%",
            overflow: "auto",
            fontSize: "0.75rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            opacity: 0.85,
          }}
        >
          {query}
        </pre>
      ) : null}
    </div>
  );
}

export function QueryErrorBanner({
  error,
  query,
  lastRefreshedAt,
}: {
  error: string;
  query?: string;
  lastRefreshedAt?: number;
}) {
  const now = useNow();
  return (
    <span
      role="alert"
      className="query-error-banner"
      style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, color: ERROR_RED }}
    >
      <span title={error} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        ⚠ Refresh failed{lastRefreshedAt ? ` · data from ${formatAge(now - lastRefreshedAt)}` : ""}: {error}
      </span>
      <span style={{ fontSize: "0.8rem", opacity: 0.85, display: "flex", flexShrink: 0 }}>
        <CopyDetails report={() => queryErrorReport(error, query, lastRefreshedAt)} />
      </span>
    </span>
  );
}

export function QueryWarningBanner({
  degraded,
}: {
  degraded: { columns: string[]; labels: string[]; reason: string };
}) {
  const names = degraded.labels.length ? degraded.labels : degraded.columns;
  const shown = `${names.map((n) => `"${n}"`).join(", ")} ${names.length === 1 ? "column does" : "columns do"} not exist on this server.`;
  return (
    <span
      role="status"
      className="query-warning-banner"
      style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, color: WARNING_AMBER }}
    >
      <span
        title={`${degraded.reason}\nLeft out of the query: ${degraded.columns.join(", ")}`}
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        ⚠ {shown}
      </span>
      <span style={{ fontSize: "0.8rem", opacity: 0.85, display: "flex", flexShrink: 0 }}>
        <CopyDetails report={() => columnsUnavailableReport(degraded.columns, degraded.reason)} />
      </span>
    </span>
  );
}

/** A cell whose column the server cannot provide (see QueryWarningBanner). */
export function UnavailableCell({ reason }: { reason: string }) {
  return (
    <span title={reason} style={{ color: WARNING_AMBER, fontStyle: "italic", fontSize: "0.85em" }}>
      unavailable
    </span>
  );
}
